<template>
  <div class="a-notification-icon__wrapper">
    <a-icon
      :icon="notificationBellIcon"
      :width="iconSize"
      :height="iconSize"
      is-button
      fill="#fff"
      no-hover
      original
      :disabled="!isLoggedIn"
      :id="$options.consts.ACTIVATOR_ID.HEADER_NOTIFICATIONS"
      aria-label="Notifications"
      @click="onClick"
    />
    <div
      v-if="isCounterVisible"
      class="a-notification-icon__notification-area"
      @click="onClick"
    >
      <span class="a-notification-icon__notification-count">{{
        notificationCountFormatted
      }}</span>
    </div>
    <a-modal-widget
      v-model="isNotificationWindowVisible"
      :activator-selector="$options.consts.HEADER_NOTIFICATIONS_SELECTOR"
      :right="-15"
      :top-offset="modalWidgetTopOffset"
      :show-social-networks="false"
      :scroll-to-top="false"
      :scroll-top="modalWidgetScrollTop"
      :slot-selector="$options.consts.NOTIFICATIONS_SELECTOR"
      :error-message-on-slot-load-failure="
        $options.consts.NOTIFICATIONS_ERROR_MESSAGE
      "
      :data-loading="inProgress"
      class="a-notification-icon__notification-window"
    >
      <a-notifications
        :notifications="notifications"
        :in-progress="inProgress"
        class="a-notification-icon__notifications"
        @delete="onDeleteNotification"
        @clear-all="onClearAllNotifications"
        @close-modal="closeModal"
      />
    </a-modal-widget>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import AIcon, { ICON } from 'shared/AIcon'
import AModalWidget from 'modals/AModalWidget'
import { HEADER_STATE, HEADER_ICON_SIZE } from 'enums/header'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import {
  ACTIVATOR_ID,
  ACTIVATOR_SELECTOR_BY_ACTIVATOR_ID
} from 'enums/activator-id'

const SCROLL_TOP_BY_HEADER_STATE = {
  [HEADER_STATE.COLLAPSED]: -42,
  [HEADER_STATE.EXPANDED]: -64
}

const MAX_NOTIFICATION_COUNT_TO_DISPLAY = 999

export default {
  name: 'NotificationIcon',
  mixins: [hydrationHelpers],
  components: {
    AIcon,
    ANotifications: hydrateWhenVisible(
      () => import('@/components/AHeader/Shared/AHeaderActions/ANotifications'),
      { props: ['notifications', 'in-progress'] }
    ),
    AModalWidget
  },
  props: {
    notificationCount: {
      type: Number,
      default: 0
    }
  },
  consts: {
    NOTIFICATIONS_SELECTOR: '.a-notifications__wrapper',
    NOTIFICATIONS_ERROR_MESSAGE:
      'Error loading user notifications. Please try to reopen it.',
    ACTIVATOR_ID,
    HEADER_NOTIFICATIONS_SELECTOR:
      ACTIVATOR_SELECTOR_BY_ACTIVATOR_ID[ACTIVATOR_ID.HEADER_NOTIFICATIONS]
  },
  data() {
    return {
      isNotificationWindowVisible: false,
      notifications: {},
      inProgress: false
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      currentHeaderState: 'currentHeaderState'
    }),
    notificationCountFormatted() {
      return this.notificationCount > MAX_NOTIFICATION_COUNT_TO_DISPLAY
        ? `${MAX_NOTIFICATION_COUNT_TO_DISPLAY}+`
        : this.notificationCount
    },
    isCounterVisible() {
      return this.isLoggedIn && this.notificationCount > 0
    },
    modalWidgetTopOffset() {
      return this.$_hydrationHelpers_mobileOrDesktopValue(0, 37)
    },
    notificationBellIcon() {
      return this.isLoggedIn ? ICON.BELL_ACTIVE : ICON.BELL_INACTIVE
    },
    modalWidgetScrollTop() {
      return SCROLL_TOP_BY_HEADER_STATE[this.currentHeaderState]
    },
    iconSize() {
      return this.$_hydrationHelpers_mobileOrDesktopValue(
        HEADER_ICON_SIZE.MOBILE,
        HEADER_ICON_SIZE.DESKTOP
      )
    }
  },
  methods: {
    ...mapActions({
      requestBellIconItems: 'notifications/requestBellIconItems',
      requestMarkReadBellIcon: 'notifications/requestMarkReadBellIcon',
      requestDeleteBellIconItem: 'notifications/requestDeleteBellIconItem',
      requestClearAllBellIcon: 'notifications/requestClearAllBellIcon'
    }),
    onClick() {
      if (this.isNotificationWindowVisible) return

      this.requestNotifications()
      this.requestMarkReadBellIcon()
      this.isNotificationWindowVisible = true
      this.$emit('open-notifications')
    },
    async requestNotifications() {
      try {
        this.inProgress = true
        this.notifications = await this.requestBellIconItems()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    async onDeleteNotification(payload) {
      try {
        this.inProgress = true
        await this.requestDeleteBellIconItem(payload)
        await this.requestNotifications()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    async onClearAllNotifications() {
      try {
        this.inProgress = true
        await this.requestClearAllBellIcon()
        await this.requestNotifications()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    closeModal() {
      this.isNotificationWindowVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.a-notification-icon__wrapper {
  position: relative;
  cursor: pointer;

  /deep/ .a-icon {
    &.a-icon_disabled.a-icon_is-button {
      opacity: 1;
    }
  }

  .a-notification-icon__notification-area {
    position: absolute;
    top: -4px;
    right: -3px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-left: 2px;
    padding-right: 2px;
    min-width: 22px;
    height: 22px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    background: $c--notification-count;

    @include mobile {
      top: -10px;
      right: -12px;
    }
  }

  .a-notification-icon__notification-count {
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    color: $c--gray-main;
  }

  .a-notification-icon__notification-window {
    width: 604px;

    @include mobile {
      width: 100%;
      border: none;
    }
  }

  .a-notification-icon__notifications {
    padding: 39px 16px 11px;
  }
}
</style>
